// STRIKZA — SLIP REPAIR · Leg card (port slice).
//
// Redactions applied vs the v2.0 handoff prototype:
//   R1 — agent-name pills collapsed into a single generic
//        "STRIKZA Intelligence" pill. Agent ids stay in `leg.agents`
//        in the data layer for a future /admin/repair-diagnostics
//        surface but are NEVER rendered here.
//   R2 — magnitude deltas (+Npp CLV / +N% win prob) are wrapped in
//        WhyDisclosure (collapsed by default).
//   R3 — `decision: 'contested'` is now `'unresolved'`. The split-
//        view UI is repurposed: instead of "Agent A vs Agent B" it
//        shows "what we have coverage for" vs "what we don't" on
//        this fixture this week.
import { type ReactElement } from "react";
import type {
  ContextSignalKey,
  Leg,
  RepairMode,
  VoidLevel,
} from "../_stubData.ts";
import { CONTEXT_SIGNALS } from "../_stubData.ts";
import { SrIcon, fmtOdds } from "./SrIcon.tsx";
import { WhyDisclosure } from "./WhyDisclosure.tsx";

export type LegAction = "accept" | "override" | "remove" | "keep" | null;

function VoidBadge({ level, label }: { level: VoidLevel; label?: string }): ReactElement {
  const cls = level === "HIGH" ? "void-high" : level === "MEDIUM" ? "void-med" : "void-low";
  const text =
    label ?? (level === "LOW" ? "LOW VOID RISK" : level === "MEDIUM" ? "MED VOID RISK" : "HIGH VOID RISK");
  return (
    <span className={`sr-badge ${cls}`}>
      <SrIcon name={level === "HIGH" ? "alert" : level === "MEDIUM" ? "info" : "shield"} size={10} />
      {text}
    </span>
  );
}

// REDACTION 1 — generic "STRIKZA Intelligence" pill replaces the
// per-leg `A-08 Line Movement + A-10 Dead Rubber` agent labels. The
// agent ids are still in props (for future admin diagnostics) but
// must not appear in any user-facing surface.
function AdminDiagnosticsRow(): ReactElement | null {
  // TODO(repair-diagnostics): when the /admin/repair-diagnostics
  // route lands, render the agent pair (primary + secondary) here
  // for founder/admin accounts only. Until then this renders null.
  return null;
}

function StrikzaIntelligencePill({ context }: { context: ContextSignalKey }): ReactElement {
  const ctx = CONTEXT_SIGNALS[context];
  return (
    <span className="sr-agent-pair" style={{ textTransform: "uppercase" as const, letterSpacing: "0.06em" }}>
      <span className="lbl">Sourced from</span>
      <span className="sep">·</span>
      <span className="a">STRIKZA Intelligence</span>
      {ctx ? (
        <>
          <span className="sep">·</span>
          <span className="lbl">{ctx.label}</span>
        </>
      ) : null}
    </span>
  );
}

interface Props {
  leg: Leg;
  mode: RepairMode;
  action: LegAction;
  onAction: (a: LegAction) => void;
  // SR-WIRE — when true, all action buttons disable while a patch is
  // in flight (parent owns the request lifecycle).
  inflight?: boolean;
  // SR-WIRE — per-leg inline error from the last failed patch.
  actionError?: string | null;
}

export function LegCard({
  leg,
  mode,
  action,
  onAction,
  inflight = false,
  actionError = null,
}: Props): ReactElement {
  const isReplaced = leg.decision === "replace";
  const isRetained = leg.decision === "retain";
  const isFlagged = leg.decision === "flag";
  const isUnresolved = leg.decision === "unresolved";

  const tag: string =
    action === "override" ? "overridden" : action === "remove" ? "removed" : leg.decision;
  const tagLabel =
    action === "override"
      ? "OVERRIDDEN"
      : action === "remove"
        ? "REMOVED"
        : isUnresolved
          ? "UNRESOLVED · YOU DECIDE"
          : isReplaced
            ? `REPLACED · ${mode === "sharp" ? "SHARP" : "SAFER"}`
            : isRetained
              ? "RETAINED"
              : "REVIEW NEEDED";

  const repair = leg.repair ? leg.repair[mode] : null;
  const altRepair =
    isReplaced && leg.altMode && leg.repair ? leg.repair[mode === "sharp" ? "safer" : "sharp"] : null;
  const ctx = leg.contextSignal ? CONTEXT_SIGNALS[leg.contextSignal] : null;
  const conf = leg.repairConfidence;

  return (
    <div className={`sr-leg ${tag} ${action === "accept" ? "accepted" : ""}`}>
      <div className="sr-leg-head">
        <span className="sr-leg-num">LEG {String(leg.n).padStart(2, "0")}</span>
        <span className="sr-leg-fixture">
          {leg.fixture.home}
          <span className="vs">vs</span>
          {leg.fixture.away}
        </span>
        <span className="sr-leg-meta">
          {leg.league}
          <span className="sep">·</span>
          {leg.kickoff}
        </span>
        <span
          className={`sr-leg-tag ${tag === "overridden" || tag === "removed" ? "" : tag === "unresolved" ? "contested" : tag}`}
        >
          <span className="pip" />
          {tagLabel}
        </span>
      </div>

      {/* R1: single generic source pill (was: per-leg agent ids) */}
      <div className="sr-agents-row">
        <StrikzaIntelligencePill context={leg.contextSignal} />
        {ctx && (
          <span className={`sr-ctx-pill ${ctx.tone}`}>
            <span className="dot" />
            {ctx.label}
          </span>
        )}
        <span className={`sr-conf-badge ${conf.toLowerCase()}`}>
          <span className="pip" />
          {conf} CONFIDENCE
        </span>
      </div>
      <AdminDiagnosticsRow />

      {/* REPLACED body */}
      {isReplaced && repair && (
        <div className="sr-leg-body">
          <div className={`sr-pick original ${action !== "override" ? "muted" : ""}`}>
            <div className="sr-pick-kicker">
              <span className="lbl">YOUR PICK</span>
            </div>
            <div className="sr-pick-market">{leg.original.market}</div>
            <div className="sr-pick-selection">{leg.original.selection}</div>
            <div className="sr-pick-bottom">
              <span className="sr-pick-odds">{fmtOdds(leg.original.odds)}</span>
              <VoidBadge level={leg.original.voidRisk} />
              {leg.original.excludedMarket && (
                <span className="sr-excluded-chip">
                  <SrIcon name="x" size={9} />
                  Excluded market
                </span>
              )}
            </div>
          </div>

          <div className="sr-leg-arrow">
            <span className="lbl">SWAP</span>
            <SrIcon name="arrowR" size={18} />
          </div>

          <div className={`sr-pick repaired ${mode}`}>
            <div className="sr-pick-kicker">
              <span className="lbl">{mode === "sharp" ? "SHARP REPAIR" : "SAFER REPAIR"}</span>
            </div>
            <div className="sr-pick-market">{repair.market}</div>
            <div className="sr-pick-selection">{repair.selection}</div>
            <div className="sr-pick-bottom">
              <span
                className="sr-pick-odds"
                style={{ color: mode === "sharp" ? "var(--sr-sharp)" : "var(--sr-safer)" }}
              >
                {fmtOdds(repair.odds)}
              </span>
              {/* R2: deltas hidden until the user reveals them */}
              <WhyDisclosure>
                {mode === "sharp" ? (
                  <span className="sr-badge clv-pos">
                    <SrIcon name="bolt" size={9} />
                    +{(repair as { clvDelta: number }).clvDelta}pp CLV
                  </span>
                ) : (
                  <span className="sr-badge prob-pos">
                    <SrIcon name="shield" size={9} />
                    +{(repair as { probDelta: number }).probDelta}% win prob
                  </span>
                )}
              </WhyDisclosure>
              <span className="sr-badge no-void">
                <SrIcon name="check" size={9} />
                Cannot void
              </span>
            </div>
          </div>
        </div>
      )}

      {isRetained && (
        <div className="sr-leg-body retained">
          <div className={`sr-pick original ${repair ? "" : ""}`}>
            <div className="sr-pick-kicker">
              <span className="lbl" style={{ color: "var(--sr-retain)" }}>
                YOUR PICK · STRIKZA AGREES
              </span>
            </div>
            <div className="sr-pick-market">{leg.original.market}</div>
            <div className="sr-pick-selection">{leg.original.selection}</div>
            <div className="sr-pick-bottom">
              <span className="sr-pick-odds">{fmtOdds(leg.original.odds)}</span>
              <VoidBadge level={leg.original.voidRisk} />
              <span className="sr-badge strong">
                <SrIcon name="check" size={9} />
                Strong edge
              </span>
            </div>
          </div>

          {repair && (
            <>
              <div className="sr-leg-arrow">
                <span className="lbl">OR</span>
                <SrIcon name="arrowR" size={18} />
              </div>

              <div className={`sr-pick repaired ${mode}`} style={{ opacity: 0.85 }}>
                <div className="sr-pick-kicker">
                  <span className="lbl">ALSO CONSIDER</span>
                </div>
                <div className="sr-pick-market">{repair.market}</div>
                {repair.selection && !/^[a-z][a-z0-9_]+$/.test(repair.selection) && (
                  <div className="sr-pick-selection">{repair.selection}</div>
                )}
                <div className="sr-pick-bottom">
                  <span
                    className="sr-pick-odds"
                    style={{ color: mode === "sharp" ? "var(--sr-sharp)" : "var(--sr-safer)" }}
                  >
                    {fmtOdds(repair.odds)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {isFlagged && (
        <div className="sr-leg-body flagged">
          <div className="sr-pick">
            <div className="sr-pick-kicker">
              <span className="lbl" style={{ color: "var(--sr-flag)" }}>
                REVIEW RECOMMENDED — NO REPLACEMENT FOUND
              </span>
            </div>
            <div className="sr-pick-market">{leg.original.market}</div>
            <div className="sr-pick-selection">{leg.original.selection}</div>
            <div className="sr-pick-bottom">
              <span className="sr-pick-odds">{fmtOdds(leg.original.odds)}</span>
              <span className="sr-badge flag">
                <SrIcon name="alert" size={9} />
                No qualifying replacement
              </span>
            </div>
          </div>
        </div>
      )}

      {/* R3: repurposed split — "covered" vs "missing" coverage on
              this fixture, this week. Same UI shell as the old
              CONTESTED branch, completely different semantics. */}
      {isUnresolved && leg.unresolvedReason && leg.repair && (
        <div className="sr-contested">
          <div className="sr-contested-head">
            <span className="kicker">UNRESOLVED · we tell you what we know</span>
            <span>
              We have substrate coverage on one market for this fixture this week but not the other. The system will
              not synthesise the missing side — your judgement wins.
            </span>
          </div>
          <div className="sr-contested-original">
            <span className="lbl">YOUR PICK</span>
            <span className="pick">
              {leg.original.selection} · {leg.original.market}
            </span>
            <VoidBadge level={leg.original.voidRisk} />
            <span className="odds">{fmtOdds(leg.original.odds)}</span>
          </div>
          <div className="sr-vote-grid">
            <div className="sr-vote">
              <div className="sr-vote-head">
                <span className="sr-vote-agent">COVERED</span>
                <span className="sr-vote-side replace">WE HAVE THIS</span>
              </div>
              <div className="sr-vote-pick">
                Swap to {leg.repair[mode].selection}
                <span className="odds">{fmtOdds(leg.repair[mode].odds)}</span>
              </div>
              <div className="sr-vote-msg">{leg.unresolvedReason.covered}</div>
            </div>
            <div className="sr-vote">
              <div className="sr-vote-head">
                <span className="sr-vote-agent">MISSING</span>
                <span className="sr-vote-side retain">NO COVERAGE</span>
              </div>
              <div className="sr-vote-pick">Keep your original pick</div>
              <div className="sr-vote-msg">{leg.unresolvedReason.missing}</div>
            </div>
          </div>
          <div className="sr-contested-choose">
            <SrIcon name="info" size={13} />
            <span>
              <span className="strong">You decide.</span> Honest substrate: we never synthesise coverage we don't
              have.
            </span>
          </div>
        </div>
      )}

      <div className="sr-reasoning">
        <div className="sr-reasoning-ic">
          <SrIcon
            name={isReplaced ? "wrench" : isRetained ? "check" : isUnresolved ? "info" : "alert"}
            size={12}
          />
        </div>
        <div className="sr-reasoning-text">
          <span className="h">{leg.reasoning.headline}</span> {leg.reasoning.body}
        </div>
      </div>

      {/* Alt mode strip — R2: delta hidden under WhyDisclosure */}
      {isReplaced && altRepair && leg.altMode && (
        <div className="sr-altmode">
          <span className={`lbl ${mode === "sharp" ? "safer" : "sharp"}`}>
            <span className="pip" />
            {mode === "sharp" ? "SAFER MODE WOULD" : "SHARP MODE WOULD"}
          </span>
          <span className="body">
            swap to <span className="pick">{altRepair.selection}</span> at {fmtOdds(altRepair.odds)}
            {" · "}
            <WhyDisclosure>
              {mode === "sharp" ? (
                <span style={{ color: "var(--sr-safer)" }}>
                  +{(altRepair as { probDelta: number }).probDelta}% landing probability
                </span>
              ) : (
                <span style={{ color: "var(--sr-sharp)" }}>
                  +{(altRepair as { clvDelta: number }).clvDelta}pp CLV
                </span>
              )}
            </WhyDisclosure>
          </span>
          <button
            className="swap"
            onClick={() => {
              // TODO(repair-actions): wire to backend mode swap.
              // eslint-disable-next-line no-console
              console.log("[repair] alt-mode swap", { legId: leg.id });
            }}
          >
            <SrIcon name="refresh" size={10} />
            Use {mode === "sharp" ? "Safer" : "Sharp"} here
          </button>
        </div>
      )}

      {actionError && (
        <div
          role="alert"
          style={{
            margin: "8px 12px 0",
            padding: "8px 10px",
            border: "1px solid var(--neg, #d62828)",
            color: "var(--neg, #d62828)",
            borderRadius: 4,
            fontSize: 11.5,
          }}
        >
          Action failed: {actionError}
        </div>
      )}

      <div className="sr-actions" data-inflight={inflight ? "1" : "0"}>
        {isFlagged ? (
          <>
            <button
              disabled={inflight}
              className={`sr-act ${action === "remove" ? "primary" : ""} danger`}
              onClick={() => onAction("remove")}
            >
              <SrIcon name="x" size={11} />
              {inflight && action === "remove" ? "Saving…" : "Remove leg"}
            </button>
            <button
              disabled={inflight}
              className={`sr-act ${action === "keep" ? "primary" : "ghost"}`}
              onClick={() => onAction("keep")}
            >
              <SrIcon name="undo" size={11} />
              {inflight && action === "keep" ? "Saving…" : "Keep anyway"}
            </button>
          </>
        ) : isUnresolved ? (
          <>
            <button
              disabled={inflight}
              className={`sr-act ${action === "accept" ? "primary" : ""}`}
              onClick={() => onAction("accept")}
            >
              <SrIcon name="wrench" size={11} />
              {inflight && action === "accept" ? "Saving…" : "Accept covered swap"}
            </button>
            <button
              disabled={inflight}
              className={`sr-act ${action === "override" ? "primary" : ""}`}
              onClick={() => onAction("override")}
            >
              <SrIcon name="check" size={11} />
              {inflight && action === "override" ? "Saving…" : "Keep my pick"}
            </button>
            <button
              disabled={inflight}
              className={`sr-act ${action === "remove" ? "primary" : ""} danger`}
              onClick={() => onAction("remove")}
            >
              <SrIcon name="x" size={11} />
              {inflight && action === "remove" ? "Saving…" : "Remove"}
            </button>
          </>
        ) : isReplaced ? (
          <>
            <button
              disabled={inflight}
              className={`sr-act ${action === "accept" || !action ? "primary" : ""}`}
              onClick={() => onAction("accept")}
            >
              <SrIcon name="check" size={11} />
              {inflight && action === "accept" ? "Saving…" : "Accept repair"}
            </button>
            <button
              disabled={inflight}
              className={`sr-act ${action === "override" ? "primary" : ""}`}
              onClick={() => onAction("override")}
            >
              <SrIcon name="undo" size={11} />
              {inflight && action === "override" ? "Saving…" : "Keep original"}
            </button>
            <button
              disabled={inflight}
              className={`sr-act ${action === "remove" ? "primary" : ""} danger`}
              onClick={() => onAction("remove")}
            >
              <SrIcon name="x" size={11} />
              {inflight && action === "remove" ? "Saving…" : "Remove"}
            </button>
          </>
        ) : (
          <>
            <button
              disabled={inflight}
              className={`sr-act ${!action || action === "keep" ? "primary" : ""}`}
              onClick={() => onAction("keep")}
            >
              <SrIcon name="check" size={11} />
              {inflight && action === "keep" ? "Saving…" : "Keep original"}
            </button>
            {repair && (
              <button
                disabled={inflight}
                className={`sr-act ${action === "accept" ? "primary" : ""}`}
                onClick={() => onAction("accept")}
              >
                <SrIcon name="wrench" size={11} />
                {inflight && action === "accept" ? "Saving…" : "Use suggestion"}
              </button>
            )}
            <button
              disabled={inflight}
              className={`sr-act ${action === "remove" ? "primary" : ""} danger`}
              onClick={() => onAction("remove")}
            >
              <SrIcon name="x" size={11} />
              {inflight && action === "remove" ? "Saving…" : "Remove"}
            </button>
          </>
        )}
        <div className="spacer" />
        <span className="leg-status">
          {action === "remove" && (
            <>
              <span className="strong" style={{ color: "var(--neg)" }}>
                Removed
              </span>{" "}
              from the repaired slip
            </>
          )}
          {action === "override" && (
            <>
              <span className="strong">Kept your original</span> selection
            </>
          )}
          {action === "keep" && (
            <>
              <span className="strong" style={{ color: "var(--warn)" }}>
                Kept flagged leg
              </span>{" "}
              against advice
            </>
          )}
          {(!action || action === "accept") && isReplaced && (
            <>
              <span className="strong" style={{ color: "var(--pos)" }}>
                Accepted
              </span>{" "}
              · contributes to repaired odds
            </>
          )}
          {(!action || action === "keep") && isRetained && (
            <>
              <span className="strong" style={{ color: "var(--pos)" }}>
                Keeping original
              </span>{" "}
              · STRIKZA agrees
            </>
          )}
          {action === "accept" && isRetained && (
            <>
              <span className="strong" style={{ color: "var(--pos)" }}>
                Using suggestion
              </span>{" "}
              · contributes to repaired odds
            </>
          )}
          {!action && isFlagged && (
            <>
              <span className="strong" style={{ color: "var(--warn)" }}>
                Awaiting your decision
              </span>
            </>
          )}
          {!action && isUnresolved && (
            <>
              <span className="strong" style={{ color: "var(--info)" }}>
                Awaiting your decision
              </span>
            </>
          )}
        </span>
      </div>
    </div>
  );
}
